import React, { FormEventHandler, Ref, forwardRef, type JSX } from "react";
import cx from "classnames";

import InputText from "components/input/InputText";
import InputTextarea from "components/input/InputTextarea";

import styles from "./styles.module.css";
import Label, { LabelStyle } from "./Label";

type InputTypes =
  | {
      value?: string;
      type: "text";
      inputRef?: Ref<HTMLInputElement>;
      onChange?: FormEventHandler<HTMLInputElement>;
      readOnly?: boolean;
    }
  | {
      value?: string;
      type: "password";
      inputRef?: Ref<HTMLInputElement>;
      onChange?: FormEventHandler<HTMLInputElement>;
      readOnly?: boolean;
    }
  | {
      value?: string;
      type: "textarea";
      inputRef?: Ref<HTMLTextAreaElement>;
      onChange?: FormEventHandler<HTMLTextAreaElement>;
      readOnly?: never;
    }
  | {
      value?: undefined;
      type?: undefined;
      inputRef?: never;
      onChange?: never;
      readOnly?: never;
    };

type FormGroupProps = {
  labelIcon?: JSX.Element;
  labelStyle?: LabelStyle;
  labelText?: string;
  top?: boolean;
  errorMessage?: string;
  divided?: boolean;
  children?: React.ReactNode;
  labelClassName?: string;
  className?: string;
  id?: string;
  placeholder?: string;
  autoComplete?: string;
  name?: string;
  fullWidth?: boolean;
} & InputTypes;

const FormGroup = forwardRef(function FormGroup(
  props: FormGroupProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    autoComplete,
    children,
    divided,
    inputRef,
    labelIcon,
    labelStyle,
    labelText,
    name,
    placeholder,
    top,
    value,
    errorMessage,
    labelClassName,
    type,
    className,
    onChange,
    fullWidth = true,
    ...restProps
  } = props;

  const hasError = !!errorMessage;

  const groupStyle = cx(styles.group, className, {
    [styles.top]: top,
    [styles.divided]: divided,
    [styles.withError]: hasError,
    [styles.withoutLabel]: !labelText,
    [styles.fullWidth]: fullWidth,
  });

  return (
    <div className={groupStyle} ref={ref}>
      {labelText && (
        <Label
          id={restProps.id}
          text={labelText}
          icon={labelIcon}
          styleProps={labelStyle}
          className={labelClassName}
        />
      )}

      {type === "password" && (
        <InputText
          {...restProps}
          className={styles.groupInput}
          name={name}
          ref={inputRef}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}

      {type === "text" && (
        <InputText
          {...restProps}
          autoComplete={autoComplete}
          className={styles.groupInput}
          name={name}
          ref={inputRef}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}

      {type === "textarea" && (
        <InputTextarea
          id={restProps.id}
          className={styles.groupInput}
          onChange={onChange}
          placeholder={placeholder}
          ref={inputRef}
        />
      )}

      {children}
      {hasError && <p className={styles.groupError}>{errorMessage}</p>}
    </div>
  );
});

export default FormGroup;
